import React from "react";
import { useQuery } from "react-query";

import { fetchVendors } from "@maeva/api/vendor";
import { Typography } from "@mui/material";
import { Skeleton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { CheckboxDropdown } from "../atoms";

const useStyles = makeStyles({
  root: {
    width: "100%",
    paddingLeft: 0,
    paddingRight: 0,
  },
});

/**
 * @param {string[]} value vendor ids to check by default
 * @param {(state) => {}} onChange called on change with an array of vendor names
 *   ["vendor1", "vendor2"] that are checked or [] if all/none are checked.
 * @param {*} props passed to `CheckboxDropdown`
 */
export default function VendorSelector({ value, onChange, ...props }) {
  const classes = useStyles();
  const { isLoading, isError, data, error } = useQuery(
    ["vendors"],
    fetchVendors
  );
  let content = null;

  if (isLoading) {
    content = (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={30}
        animation="wave"
      />
    );
  } else if (isError || (data && data.length === 0)) {
    console.error("Failed to load vendor data, error:", error);
    content = (
      <Typography variant="body1">
        Vendor filter unavailable, try reloading
      </Typography>
    );
  } else if (data) {
    // Sort alphanumerically, ignoring case
    const choices = data
      .map((vendor) => vendor.name)
      .sort((a, b) => a.localeCompare(b));

    // Get the currently selected names from their corresponding ids
    const getVendorNameById = (id) => data.find((v) => v.id === id).name;
    const defaultCheckedNames = value.length
      ? value.map(getVendorNameById)
      : choices;

    const handleChange = (checked) => {
      if (!onChange) {
        return;
      } else if (checked.length === choices.length) {
        // If all are checked, there's nothing to filter by
        onChange([]);
        return;
      }

      const getVendorIdByName = (name) => data.find((v) => v.name === name).id;

      onChange(checked.map(getVendorIdByName));
    };

    content = (
      <CheckboxDropdown
        className={classes.root}
        choices={choices}
        checkAllByDefault
        defaultChecked={defaultCheckedNames}
        cannotBeEmpty
        onChange={handleChange}
        aria-labelledby="vendors-label"
        {...props}
      />
    );
  }

  return (
    <>
      <Typography id="vendors-label" variant="subtitle1" color="secondary">
        From these vendors
      </Typography>
      {content}
    </>
  );
}
